import React, { useCallback, useEffect, useState } from 'react';
import { adminApi } from '../../utils/api';
import { Modal, Spinner } from './shared';

function KeyTypeBadge({ type }) {
    const colors = {
        string: 'bg-blue-100 text-blue-700',
        list: 'bg-purple-100 text-purple-700',
        set: 'bg-green-100 text-green-700',
        zset: 'bg-yellow-100 text-yellow-700',
        hash: 'bg-orange-100 text-orange-700',
    };
    return (
        <span className={`px-1.5 py-0.5 text-[10px] font-mono rounded ${colors[type] || 'bg-gray-100 text-gray-600'}`}>
            {type}
        </span>
    );
}

function formatTTL(ttl) {
    if (ttl === -1) return 'no expiry';
    if (ttl === -2) return 'expired';
    if (ttl < 60) return `${ttl}s`;
    if (ttl < 3600) return `${Math.floor(ttl / 60)}m ${ttl % 60}s`;
    return `${Math.floor(ttl / 3600)}h ${Math.floor((ttl % 3600) / 60)}m`;
}

function JsonView({ data, depth = 0 }) {
    if (data === null || data === undefined) return <span className="text-gray-400">null</span>;
    if (typeof data === 'boolean') return <span className="text-purple-600">{String(data)}</span>;
    if (typeof data === 'number') return <span className="text-blue-600">{data}</span>;
    if (typeof data === 'string') {
        if (data.length > 300) {
            return <span className="text-green-700 break-all">"{data.slice(0, 300)}..."</span>;
        }
        return <span className="text-green-700 break-all">"{data}"</span>;
    }

    if (Array.isArray(data)) {
        if (data.length === 0) return <span className="text-gray-400">[]</span>;
        return (
            <div className={depth > 0 ? 'ml-4' : ''}>
                <span className="text-gray-500">[</span>
                {data.slice(0, 50).map((item, i) => (
                    <div key={i} className="ml-4">
                        <JsonView data={item} depth={depth + 1} />
                        {i < Math.min(data.length, 50) - 1 && <span className="text-gray-400">,</span>}
                    </div>
                ))}
                {data.length > 50 && <div className="ml-4 text-gray-400">... ({data.length - 50} more)</div>}
                <span className="text-gray-500">]</span>
            </div>
        );
    }

    if (typeof data === 'object') {
        const entries = Object.entries(data);
        if (entries.length === 0) return <span className="text-gray-400">{'{}'}</span>;
        return (
            <div className={depth > 0 ? 'ml-4' : ''}>
                <span className="text-gray-500">{'{'}</span>
                {entries.slice(0, 50).map(([k, v], i) => (
                    <div key={k} className="ml-4">
                        <span className="text-red-600">"{k}"</span>
                        <span className="text-gray-500">: </span>
                        <JsonView data={v} depth={depth + 1} />
                        {i < Math.min(entries.length, 50) - 1 && <span className="text-gray-400">,</span>}
                    </div>
                ))}
                {entries.length > 50 && <div className="ml-4 text-gray-400">... ({entries.length - 50} more)</div>}
                <span className="text-gray-500">{'}'}</span>
            </div>
        );
    }

    return <span>{String(data)}</span>;
}

function KeyDetailModal({ keyName, open, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!open || !keyName) return;
        setLoading(true);
        setData(null);
        adminApi.getRedisKeyDetail(keyName)
            .then(setData)
            .catch(e => console.error('Failed to fetch key detail:', e))
            .finally(() => setLoading(false));
    }, [open, keyName]);

    return (
        <Modal open={open} onClose={onClose} title={keyName} size="xl">
            <Modal.Body>
                {loading ? (
                    <Spinner size="sm" text="Loading key..." />
                ) : !data ? (
                    <p className="text-sm text-gray-500">Failed to load key data</p>
                ) : (
                    <div className="space-y-4">
                        <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">Type:</span>
                                <KeyTypeBadge type={data.type} />
                            </div>
                            <div>
                                <span className="text-gray-500">TTL: </span>
                                <span className="font-mono text-gray-700">{formatTTL(data.ttl)}</span>
                            </div>
                            {data.size_bytes != null && (
                                <div>
                                    <span className="text-gray-500">Size: </span>
                                    <span className="font-mono text-gray-700">{data.size_bytes} bytes</span>
                                </div>
                            )}
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-auto max-h-[60vh]">
                            <pre className="text-xs font-mono leading-relaxed whitespace-pre-wrap">
                                <JsonView data={data.value} />
                            </pre>
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}

export default function AdminRedisCache() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pattern, setPattern] = useState('*');
    const [inputPattern, setInputPattern] = useState('*');
    const [cursor, setCursor] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [history, setHistory] = useState([]);
    const [selectedKey, setSelectedKey] = useState(null);

    const fetchKeys = useCallback(async (pat, cur = 0, append = false) => {
        setLoading(true);
        try {
            const res = await adminApi.listRedisKeys({ pattern: pat, cursor: cur, limit: 50 });
            setItems(prev => append ? [...prev, ...res.items] : res.items);
            setCursor(res.next_cursor);
            setHasMore(res.has_more);
        } catch (e) {
            console.error('Failed to fetch Redis keys:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchKeys(pattern);
    }, [pattern, fetchKeys]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPattern(inputPattern);
        setHistory(prev => {
            const cleaned = prev.filter(p => p !== inputPattern);
            return [inputPattern, ...cleaned].slice(0, 10);
        });
    };

    const handleLoadMore = () => {
        fetchKeys(pattern, cursor, true);
    };

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-xl font-semibold text-gray-900">Redis Cache Browser</h1>
                <p className="text-sm text-gray-500 mt-0.5">Browse and inspect all Redis keys</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-4 py-3 border-b border-gray-100">
                    <form onSubmit={handleSearch} className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                            </svg>
                            <input
                                type="text"
                                value={inputPattern}
                                onChange={(e) => setInputPattern(e.target.value)}
                                placeholder="Key pattern (e.g. worker:*, cache:sync:*)"
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
                        >
                            Search
                        </button>
                    </form>
                    {history.length > 0 && (
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                            <span className="text-xs text-gray-400">Recent:</span>
                            {history.map(p => (
                                <button
                                    key={p}
                                    onClick={() => { setInputPattern(p); setPattern(p); }}
                                    className="px-2 py-0.5 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100"
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="divide-y divide-gray-50">
                    {loading && items.length === 0 ? (
                        <div className="flex justify-center py-12">
                            <Spinner size="sm" text="Scanning keys..." />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <p className="text-sm">No keys found for pattern: <span className="font-mono">{pattern}</span></p>
                        </div>
                    ) : (
                        <>
                            <table className="w-full">
                                <thead>
                                    <tr className="text-left text-xs text-gray-400 uppercase tracking-wider">
                                        <th className="px-4 py-2 font-medium">Key</th>
                                        <th className="px-4 py-2 font-medium w-20">Type</th>
                                        <th className="px-4 py-2 font-medium w-28 text-right">TTL</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {items.map(item => (
                                        <tr
                                            key={item.key}
                                            onClick={() => setSelectedKey(item.key)}
                                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                                        >
                                            <td className="px-4 py-2.5">
                                                <span className="text-sm font-mono text-gray-700 break-all">{item.key}</span>
                                            </td>
                                            <td className="px-4 py-2.5">
                                                <KeyTypeBadge type={item.type} />
                                            </td>
                                            <td className="px-4 py-2.5 text-right">
                                                <span className="text-xs text-gray-500 font-mono">{formatTTL(item.ttl)}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {hasMore && (
                                <div className="px-4 py-3 text-center">
                                    <button
                                        onClick={handleLoadMore}
                                        disabled={loading}
                                        className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                                    >
                                        {loading ? 'Loading...' : 'Load more'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <KeyDetailModal
                keyName={selectedKey}
                open={!!selectedKey}
                onClose={() => setSelectedKey(null)}
            />
        </div>
    );
}
